/* eslint-disable no-bitwise, no-plusplus, no-param-reassign, no-continue */

const VERSION = 10
const SIZE = VERSION * 4 + 17
const QUIET_ZONE = 4
const DATA_CODEWORDS = 274
const ECC_CODEWORDS_PER_BLOCK = 18
const BLOCK_DATA_LENGTHS = [68, 68, 69, 69]
const ALIGNMENT_PATTERN_POSITIONS = [6, 28, 50]
const FORMAT_ECC_LOW = 1

let gfExp
let gfLog

function ensureGaloisTables() {
  if (gfExp && gfLog) return

  gfExp = new Array(512)
  gfLog = new Array(256)

  let value = 1
  for (let i = 0; i < 255; i++) {
    gfExp[i] = value
    gfLog[value] = i
    value <<= 1
    if (value & 0x100) value ^= 0x11d
  }

  for (let i = 255; i < 512; i++) {
    gfExp[i] = gfExp[i - 255]
  }
}

function multiplyGalois(left, right) {
  if (left === 0 || right === 0) return 0
  ensureGaloisTables()
  return gfExp[gfLog[left] + gfLog[right]]
}

function buildReedSolomonDivisor(degree) {
  const result = new Array(degree).fill(0)
  result[degree - 1] = 1

  let root = 1
  for (let i = 0; i < degree; i++) {
    for (let j = 0; j < degree; j++) {
      result[j] = multiplyGalois(result[j], root)
      if (j + 1 < degree) result[j] ^= result[j + 1]
    }
    root = multiplyGalois(root, 0x02)
  }

  return result
}

function computeReedSolomonRemainder(data, divisor) {
  const result = new Array(divisor.length).fill(0)

  data.forEach(byte => {
    const factor = byte ^ result.shift()
    result.push(0)
    divisor.forEach((coefficient, index) => {
      result[index] ^= multiplyGalois(coefficient, factor)
    })
  })

  return result
}

function appendBits(bits, value, length) {
  for (let i = length - 1; i >= 0; i--) {
    bits.push((value >>> i) & 1)
  }
}

function bitsToCodewords(bits) {
  const codewords = []
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0
    for (let j = 0; j < 8; j++) {
      byte = (byte << 1) | bits[i + j]
    }
    codewords.push(byte)
  }
  return codewords
}

function encodeData(text) {
  const bytes = Array.from(new TextEncoder().encode(text))
  const maxBytes = Math.floor((DATA_CODEWORDS * 8 - 4 - 16) / 8)

  if (bytes.length > maxBytes) {
    throw new Error('QR payload is too long.')
  }

  const bits = []
  appendBits(bits, 0x04, 4)
  appendBits(bits, bytes.length, 16)
  bytes.forEach(byte => appendBits(bits, byte, 8))

  const capacityBits = DATA_CODEWORDS * 8
  appendBits(bits, 0, Math.min(4, capacityBits - bits.length))
  while (bits.length % 8 !== 0) bits.push(0)

  const codewords = bitsToCodewords(bits)
  for (let padByte = 0xec; codewords.length < DATA_CODEWORDS; padByte ^= 0xfd) {
    codewords.push(padByte)
  }

  return codewords
}

function addErrorCorrection(dataCodewords) {
  const divisor = buildReedSolomonDivisor(ECC_CODEWORDS_PER_BLOCK)
  const blocks = []
  let offset = 0

  BLOCK_DATA_LENGTHS.forEach(length => {
    const data = dataCodewords.slice(offset, offset + length)
    offset += length
    blocks.push({
      data,
      ecc: computeReedSolomonRemainder(data, divisor),
    })
  })

  const result = []
  const maxDataLength = Math.max(...BLOCK_DATA_LENGTHS)

  for (let i = 0; i < maxDataLength; i++) {
    blocks.forEach(block => {
      if (i < block.data.length) result.push(block.data[i])
    })
  }

  for (let i = 0; i < ECC_CODEWORDS_PER_BLOCK; i++) {
    blocks.forEach(block => {
      result.push(block.ecc[i])
    })
  }

  return result
}

function createMatrix() {
  return {
    modules: Array.from({ length: SIZE }, () => new Array(SIZE).fill(false)),
    reserved: Array.from({ length: SIZE }, () => new Array(SIZE).fill(false)),
  }
}

function isInBounds(x, y) {
  return x >= 0 && x < SIZE && y >= 0 && y < SIZE
}

function setFunctionModule(matrix, x, y, dark) {
  if (!isInBounds(x, y)) return
  matrix.modules[y][x] = dark
  matrix.reserved[y][x] = true
}

function addFinderPattern(matrix, x, y) {
  for (let dy = -1; dy <= 7; dy++) {
    for (let dx = -1; dx <= 7; dx++) {
      const distance = Math.max(Math.abs(dx - 3), Math.abs(dy - 3))
      const dark = dx >= 0 && dx <= 6 && dy >= 0 && dy <= 6 && distance !== 2
      setFunctionModule(matrix, x + dx, y + dy, dark)
    }
  }
}

function addAlignmentPattern(matrix, centerX, centerY) {
  for (let dy = -2; dy <= 2; dy++) {
    for (let dx = -2; dx <= 2; dx++) {
      const distance = Math.max(Math.abs(dx), Math.abs(dy))
      setFunctionModule(matrix, centerX + dx, centerY + dy, distance !== 1)
    }
  }
}

function reserveFormatInfo(matrix) {
  for (let i = 0; i <= 8; i++) {
    if (i !== 6) {
      setFunctionModule(matrix, 8, i, false)
      setFunctionModule(matrix, i, 8, false)
    }
  }

  for (let i = 0; i < 8; i++) {
    setFunctionModule(matrix, SIZE - 1 - i, 8, false)
  }

  for (let i = 0; i < 7; i++) {
    setFunctionModule(matrix, 8, SIZE - 1 - i, false)
  }

  setFunctionModule(matrix, 8, SIZE - 8, true)
}

function reserveVersionInfo(matrix) {
  for (let i = 0; i < 6; i++) {
    for (let j = 0; j < 3; j++) {
      setFunctionModule(matrix, SIZE - 11 + j, i, false)
      setFunctionModule(matrix, i, SIZE - 11 + j, false)
    }
  }
}

function addFunctionPatterns(matrix) {
  addFinderPattern(matrix, 0, 0)
  addFinderPattern(matrix, SIZE - 7, 0)
  addFinderPattern(matrix, 0, SIZE - 7)

  ALIGNMENT_PATTERN_POSITIONS.forEach(x => {
    ALIGNMENT_PATTERN_POSITIONS.forEach(y => {
      const overlapsFinder = (x === 6 && y === 6)
        || (x === 6 && y === 50)
        || (x === 50 && y === 6)
      if (!overlapsFinder) addAlignmentPattern(matrix, x, y)
    })
  })

  for (let i = 8; i < SIZE - 8; i++) {
    const dark = i % 2 === 0
    setFunctionModule(matrix, i, 6, dark)
    setFunctionModule(matrix, 6, i, dark)
  }

  reserveFormatInfo(matrix)
  reserveVersionInfo(matrix)
}

function getMaskBit(mask, x, y) {
  switch (mask) {
    case 0: return (x + y) % 2 === 0
    case 1: return y % 2 === 0
    case 2: return x % 3 === 0
    case 3: return (x + y) % 3 === 0
    case 4: return (Math.floor(y / 2) + Math.floor(x / 3)) % 2 === 0
    case 5: return ((x * y) % 2) + ((x * y) % 3) === 0
    case 6: return (((x * y) % 2) + ((x * y) % 3)) % 2 === 0
    case 7: return (((x + y) % 2) + ((x * y) % 3)) % 2 === 0
    default: return false
  }
}

function drawDataCodewords(matrix, codewords, mask) {
  const bits = []
  codewords.forEach(codeword => appendBits(bits, codeword, 8))

  let bitIndex = 0
  let upward = true

  for (let right = SIZE - 1; right >= 1; right -= 2) {
    if (right === 6) right--

    for (let vertical = 0; vertical < SIZE; vertical++) {
      const y = upward ? SIZE - 1 - vertical : vertical

      for (let columnOffset = 0; columnOffset < 2; columnOffset++) {
        const x = right - columnOffset
        if (matrix.reserved[y][x]) continue

        let dark = bitIndex < bits.length ? bits[bitIndex] === 1 : false
        if (getMaskBit(mask, x, y)) dark = !dark
        matrix.modules[y][x] = dark
        bitIndex++
      }
    }

    upward = !upward
  }
}

function getBchCode(data, bitLength, polynomial) {
  let remainder = data
  for (let i = 0; i < bitLength; i++) {
    remainder = (remainder << 1) ^ (((remainder >>> (bitLength - 1)) & 1) * polynomial)
  }
  return (data << bitLength) | remainder
}

function drawFormatBits(matrix, mask) {
  const data = (FORMAT_ECC_LOW << 3) | mask
  const bits = getBchCode(data, 10, 0x537) ^ 0x5412

  for (let i = 0; i <= 5; i++) setFunctionModule(matrix, 8, i, ((bits >>> i) & 1) === 1)
  setFunctionModule(matrix, 8, 7, ((bits >>> 6) & 1) === 1)
  setFunctionModule(matrix, 8, 8, ((bits >>> 7) & 1) === 1)
  setFunctionModule(matrix, 7, 8, ((bits >>> 8) & 1) === 1)
  for (let i = 9; i < 15; i++) setFunctionModule(matrix, 14 - i, 8, ((bits >>> i) & 1) === 1)

  for (let i = 0; i < 8; i++) setFunctionModule(matrix, SIZE - 1 - i, 8, ((bits >>> i) & 1) === 1)
  for (let i = 8; i < 15; i++) setFunctionModule(matrix, 8, SIZE - 15 + i, ((bits >>> i) & 1) === 1)
  setFunctionModule(matrix, 8, SIZE - 8, true)
}

function drawVersionBits(matrix) {
  const bits = getBchCode(VERSION, 12, 0x1f25)

  for (let i = 0; i < 18; i++) {
    const bit = ((bits >>> i) & 1) === 1
    const a = SIZE - 11 + (i % 3)
    const b = Math.floor(i / 3)
    setFunctionModule(matrix, a, b, bit)
    setFunctionModule(matrix, b, a, bit)
  }
}

function finderPenaltyAt(line, index) {
  const pattern = [true, false, true, true, true, false, true, false, false, false, false]
  const reversePattern = [false, false, false, false, true, false, true, true, true, false, true]

  for (let i = 0; i < pattern.length; i++) {
    if (line[index + i] !== pattern[i]) break
    if (i === pattern.length - 1) return true
  }

  for (let i = 0; i < reversePattern.length; i++) {
    if (line[index + i] !== reversePattern[i]) break
    if (i === reversePattern.length - 1) return true
  }

  return false
}

function getPenaltyScore(modules) {
  let score = 0
  let darkCount = 0

  for (let y = 0; y < SIZE; y++) {
    let runColor = modules[y][0]
    let runLength = 1

    for (let x = 0; x < SIZE; x++) {
      if (modules[y][x]) darkCount++

      if (x === 0) continue
      if (modules[y][x] === runColor) {
        runLength++
      } else {
        if (runLength >= 5) score += runLength - 2
        runColor = modules[y][x]
        runLength = 1
      }
    }

    if (runLength >= 5) score += runLength - 2

    for (let x = 0; x <= SIZE - 11; x++) {
      if (finderPenaltyAt(modules[y], x)) score += 40
    }
  }

  for (let x = 0; x < SIZE; x++) {
    let runColor = modules[0][x]
    let runLength = 1
    const column = []

    for (let y = 0; y < SIZE; y++) {
      column.push(modules[y][x])
      if (y === 0) continue
      if (modules[y][x] === runColor) {
        runLength++
      } else {
        if (runLength >= 5) score += runLength - 2
        runColor = modules[y][x]
        runLength = 1
      }
    }

    if (runLength >= 5) score += runLength - 2

    for (let y = 0; y <= SIZE - 11; y++) {
      if (finderPenaltyAt(column, y)) score += 40
    }
  }

  for (let y = 0; y < SIZE - 1; y++) {
    for (let x = 0; x < SIZE - 1; x++) {
      const color = modules[y][x]
      if (
        modules[y][x + 1] === color
        && modules[y + 1][x] === color
        && modules[y + 1][x + 1] === color
      ) {
        score += 3
      }
    }
  }

  const totalModules = SIZE * SIZE
  const balancePenalty = Math.floor(Math.abs(darkCount * 20 - totalModules * 10) / totalModules)
  score += balancePenalty * 10

  return score
}

function buildQrModules(text) {
  const dataCodewords = encodeData(text)
  const codewords = addErrorCorrection(dataCodewords)
  let bestMatrix = null
  let bestScore = Infinity

  for (let mask = 0; mask < 8; mask++) {
    const matrix = createMatrix()
    addFunctionPatterns(matrix)
    drawDataCodewords(matrix, codewords, mask)
    drawFormatBits(matrix, mask)
    drawVersionBits(matrix)

    const score = getPenaltyScore(matrix.modules)
    if (score < bestScore) {
      bestScore = score
      bestMatrix = matrix.modules
    }
  }

  return bestMatrix
}

function modulesToSvgDataUri(modules) {
  const totalSize = SIZE + QUIET_ZONE * 2
  let path = ''

  modules.forEach((row, y) => {
    row.forEach((dark, x) => {
      if (dark) path += `M${x + QUIET_ZONE},${y + QUIET_ZONE}h1v1h-1z`
    })
  })

  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${totalSize} ${totalSize}" shape-rendering="crispEdges"><rect width="${totalSize}" height="${totalSize}" fill="#fff"/><path d="${path}" fill="#111827"/></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}

export default function generateQrCodeDataUri(text) {
  if (!text) return ''
  return modulesToSvgDataUri(buildQrModules(text))
}
